import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Sparkles, FileText, Save, Trash2, Globe, Copy, Eye, Printer,
  Download, ArrowLeft, ChevronRight, Plus, UserCog,
} from "lucide-react";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import { Skeleton } from "@/components/Skeleton";
import PlanUpgradeSection from "@/components/PlanUpgradeSection";

const TEMPLATES = [
  { id: "modern_minimal",     name: "Modern Minimal",     vibe: "Clean · one-page",        accent: "tb-chip-lavender" },
  { id: "academic_cv",        name: "Academic CV",        vibe: "Research & teaching",     accent: "tb-chip-sky" },
  { id: "school_teacher",     name: "School Teacher",     vibe: "Classroom impact",        accent: "tb-chip-mint" },
  { id: "university_faculty", name: "University Faculty", vibe: "Publications · courses",  accent: "tb-chip-butter" },
  { id: "creative_educator",  name: "Creative Educator",  vibe: "Portfolio-forward",       accent: "tb-chip-rose" },
  { id: "recruiter_ready",    name: "Recruiter-Ready",    vibe: "International · ATS",     accent: "tb-chip-sky" },
];

const KINDS = [
  { id: "resume",       label: "Resume" },
  { id: "cv",           label: "Academic CV" },
  { id: "biodata",      label: "Biodata" },
  { id: "portfolio",    label: "Portfolio" },
  { id: "cover_letter", label: "Cover letter" },
  { id: "philosophy",   label: "Philosophy statement" },
];

export default function ResumeBuilder() {
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);

  // Generate form state
  const [genTemplate, setGenTemplate] = useState("modern_minimal");
  const [genKind, setGenKind] = useState("resume");
  const [genAudience, setGenAudience] = useState("");
  const [genExtra, setGenExtra] = useState("");
  const [generating, setGenerating] = useState(false);

  // Save / publish state
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/resume");
      setList(data);
      if (data.length > 0 && !active) selectResume(data[0]);
    } catch { toast.error("Could not load resumes"); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const selectResume = (r) => {
    setActive(r);
    setEditTitle(r.title);
    setEditContent(r.content);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/resume/generate", {
        template: genTemplate,
        kind: genKind,
        target_audience: genAudience || undefined,
        extra_guidance: genExtra || undefined,
      });
      toast.success("Resume generated");
      setList((l) => [data, ...(l || [])]);
      selectResume(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, { action: { label: "Recharge", onClick: () => navigate("/wallet") } });
      } else {
        toast.error(typeof detail === "string" ? detail : "Generation failed");
      }
    } finally { setGenerating(false); }
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/resume/${active.resume_id}`, {
        title: editTitle, content: editContent,
      });
      setActive(data);
      setList((l) => (l || []).map((r) => r.resume_id === data.resume_id ? data : r));
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const togglePublic = async () => {
    if (!active) return;
    try {
      const { data } = await api.post(`/resume/${active.resume_id}/publish`, {
        is_public: !active.is_public, allow_download: true,
      });
      const merged = { ...active, ...data };
      setActive(merged);
      setList((l) => (l || []).map((r) => r.resume_id === merged.resume_id ? merged : r));
      if (data.is_public) {
        const url = `${window.location.origin}${data.public_url_path}`;
        try { await navigator.clipboard.writeText(url); toast.success("Published · link copied"); }
        catch { toast.success("Published"); }
      } else {
        toast.success("Made private");
      }
    } catch { toast.error("Could not update visibility"); }
  };

  const copyPublicLink = async () => {
    if (!active?.share_token) return;
    const url = `${window.location.origin}/r/${active.share_token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this resume?")) return;
    try {
      await api.delete(`/resume/${id}`);
      setList((l) => (l || []).filter((r) => r.resume_id !== id));
      if (active?.resume_id === id) setActive(null);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const printResume = () => {
    if (!printRef.current) return;
    window.print();
  };

  return (
    <Layout title="AI Resume Builder">
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div>
          <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
            <FileText className="h-3 w-3" /> Career copilot
          </span>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
            AI Resume Builder
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
            Premium educator resumes, CVs, biodata and portfolios — generated from your TeeBee profile, instantly.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/career")} data-testid="resume-career-cta">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Career Copilot
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* ===== Left: Generator + List ===== */}
        <aside className="lg:col-span-5 space-y-4 print:hidden">
          {/* Generator card */}
          <div className="tb-card p-5 space-y-3" data-testid="resume-generator">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Generate new</p>
            <div>
              <Label>Document type</Label>
              <select value={genKind} onChange={(e) => setGenKind(e.target.value)} className="mt-1.5 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm" data-testid="resume-kind">
                {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Template</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {TEMPLATES.map((t) => {
                  const active = genTemplate === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setGenTemplate(t.id)}
                      className={`text-left rounded-xl p-3 border transition ${
                        active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                      }`}
                      data-testid={`tpl-${t.id}`}
                    >
                      <span className={`inline-block h-2 w-8 rounded-full ${t.accent}`} />
                      <p className="mt-2 text-sm font-medium">{t.name}</p>
                      <p className="text-[11px] text-muted-foreground">{t.vibe}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Target audience (optional)</Label>
              <Input value={genAudience} onChange={(e) => setGenAudience(e.target.value)} placeholder="e.g. CBSE school principal · International recruiter" className="mt-1.5 h-11 rounded-xl" data-testid="resume-audience" />
            </div>
            <div>
              <Label>Extra guidance (optional)</Label>
              <Textarea rows={2} value={genExtra} onChange={(e) => setGenExtra(e.target.value)} placeholder="Highlight my STEM teaching, downplay my admin role…" className="mt-1.5 rounded-xl" data-testid="resume-extra" />
            </div>
            <Button onClick={generate} disabled={generating} className="w-full h-11 rounded-full" data-testid="resume-generate">
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              Generate · 10 coins
            </Button>
          </div>

          {/* List */}
          <div className="tb-card p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-3">Your documents</p>
            {list === null ? (
              <Skeleton className="h-20" />
            ) : list.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 italic">No resumes yet — generate your first above.</p>
            ) : (
              <ul className="space-y-1.5">
                {list.map((r) => (
                  <li key={r.resume_id} className="flex items-center gap-2">
                    <button
                      onClick={() => selectResume(r)}
                      className={`flex-1 text-left rounded-lg p-2.5 hover:bg-muted transition ${active?.resume_id === r.resume_id ? "bg-muted" : ""}`}
                      data-testid={`resume-item-${r.resume_id}`}
                    >
                      <p className="text-sm font-medium line-clamp-1">{r.title}</p>
                      <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                        {(r.kind || "resume").replace(/_/g, " ")} · {(r.template || "").replace(/_/g, " ")}
                        {r.is_public && <Globe className="h-3 w-3 text-primary" />}
                      </p>
                    </button>
                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive h-8 w-8" onClick={() => remove(r.resume_id)} aria-label="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* ===== Right: Editor + Preview ===== */}
        <section className="lg:col-span-7">
          {!active ? (
            <div className="tb-card p-10 text-center print:hidden" data-testid="resume-empty">
              <span className="mx-auto h-14 w-14 grid place-items-center rounded-full tb-ai-orb mb-3 tb-float">
                <Sparkles className="h-6 w-6 text-white" />
              </span>
              <p className="font-display text-lg font-medium">Your first AI-crafted resume is one click away</p>
              <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">
                Pick a template on the left, optionally add a target audience, and TeeBee will generate a print-ready document tailored to your profile.
              </p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-md mx-auto text-left">
                <Hint num="1" text="Fill your profile" />
                <Hint num="2" text="Pick template + kind" />
                <Hint num="3" text="Generate · 10 coins" />
              </div>
              <Button variant="outline" className="rounded-full mt-5" onClick={() => navigate("/profile")} data-testid="resume-empty-profile-cta">
                <UserCog className="h-3.5 w-3.5 mr-1.5" /> Open my profile
              </Button>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="tb-card p-4 mb-3 flex flex-wrap items-center gap-2 print:hidden" data-testid="resume-toolbar">
                <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="h-10 rounded-xl flex-1 min-w-[200px]" data-testid="resume-title" />
                <Button size="sm" className="rounded-full" onClick={save} disabled={saving} data-testid="resume-save">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                  Save
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={togglePublic} data-testid="resume-publish">
                  {active.is_public ? <><Globe className="h-3.5 w-3.5 mr-1.5 text-primary" /> Public</> : <><Globe className="h-3.5 w-3.5 mr-1.5" /> Publish</>}
                </Button>
                {active.is_public && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={copyPublicLink} data-testid="resume-copy-link">
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
                  </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-full" onClick={printResume} data-testid="resume-print">
                  <Printer className="h-3.5 w-3.5 mr-1.5" /> Print / PDF
                </Button>
              </div>

              {/* Editor + preview */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="tb-card p-5 print:hidden">
                  <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2">Markdown editor</p>
                  <Textarea
                    rows={24}
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="font-mono text-xs rounded-xl"
                    data-testid="resume-editor"
                  />
                </div>
                <div ref={printRef} className={`tb-card p-8 sm:p-10 print:p-0 print:shadow-none print:border-0 tb-resume-${active.template}`} data-testid="resume-preview" id="resume-print-area">
                  <Markdown>{editContent}</Markdown>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {/* ===== Plan upgrade nudge — only shown on the resume page since it's premium territory ===== */}
      <PlanUpgradeSection source="resume" variant="compact" id="plans" />
    </Layout>
  );
}

function Hint({ num, text }) {
  return (
    <div className="rounded-xl bg-muted/40 px-3 py-2 border border-border/60">
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary">Step {num}</p>
      <p className="text-[11px] mt-0.5">{text}</p>
    </div>
  );
}
